import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from './authContext'
import { BACKEND_URL } from '../../config'

axios.defaults.withCredentials = true

const LoginPage: React.FC = () => {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('admin@example.com')
  const [password, setPassword] = useState('password')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState<any>(null)
  const [checkingSession, setCheckingSession] = useState(true)
  const location = useLocation()

useEffect(() => {
  let isMounted = true;

  const params = new URLSearchParams(location.search);
  const code = params.get("code");
  const backendError = params.get("error");

  // If Google OAuth callback → DO NOT run session check
  if (code || backendError) {
    setCheckingSession(false);
    return;
  }

  // Normal page load → check session
  const checkSession = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/me`, {
        withCredentials: true
      });

      if (isMounted && res.data.loggedIn) {
        navigate('/');
      }
    } catch {
      // A missing session is expected on the login page.
    } finally {
      if (isMounted) setCheckingSession(false);
    }
  };

  checkSession();

  return () => {
    isMounted = false;
  };
}, [location.search]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await login(email, password)
      navigate('/')
    } catch (err: any) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = () => {
    // Redirect to backend OAuth route; backend will redirect back to frontend
    window.location.href = `${BACKEND_URL}/auth/google`
  }

  const handleLocalLogin = async () => {
    setError(null)
    setLoading(true)
    try {
      await login(email, password)
      navigate('/')
    } catch (err: any) {
      setError(err.message || 'Local login failed')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate('/login');
    } catch (err) {
      console.error('Logout failed', err)
    }
  }

  if (checkingSession) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row items-center justify-center bg-background-light dark:bg-background-dark px-4 py-6">
      <div className="relative hidden w-1/2 flex-col items-center justify-center bg-[#f0f9fc] lg:flex lg:rounded-l-xl overflow-hidden">
        <div className="absolute inset-0">
          <img alt="A doctor reviewing medical data on a tablet with a colleague." className="h-full w-full object-cover" src="https://lh3.googleusercontent.com/aida-public/AB6AXuB_eGNiwVHWQTu7GV_PRI3421AsXiNseLlsxsA8OwMsPL8rHNvjwo53MSTMmA9oQKlPDeD15X5JqqcnYPIHGTiHNVZmmO5HKfYRTTlo7k5_FJFsGjllzZ7RV4urenCyMJr3iQtzYA7xdaTYbpCB5dX0a1ehGT8_KMa3PhMbw_xueWwCNIGEXGA_yGrc3SxCUKMK_yZUGiFz449hP6VXzOTOcCi0MK6kj_ALCQfctWHC0ki69Tzl9IJlFTSPZIRpvrujY5zBnsSq_jg" />
        </div>
        <div className="absolute inset-0 bg-primary/30"></div>
        <div className="relative z-10 flex flex-col items-center text-center p-10 text-white">
          <div className="flex items-center gap-3">
            <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-4H8v-2h3V7h2v4h3v2h-3v4h-2z" fill="currentColor"></path></svg>
            <h1 className="text-4xl font-bold tracking-tight">Pranave Clinic</h1>
          </div>
          <p className="mt-4 text-lg max-w-md">Your trusted partner in healthcare management. Secure, efficient, and reliable.</p>
        </div>
      </div>
     <div className="w-full max-w-md p-6 sm:p-8 rounded-xl border">
        {!user ? (
          <>
            <h1 className="text-xl sm:text-2xl font-bold mb-2">Sign in</h1>
            <p className="text-sm text-subtle-light dark:text-subtle-dark mb-4"></p>

            {/* Google OAuth */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full h-11 rounded-md bg-white border border-border-light text-text-light font-semibold mb-3 hover:shadow-sm flex items-center justify-center gap-3"
              aria-label="Login with Google"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span className="text-sm">Sign in with Google</span>
            </button>

            {import.meta.env.DEV && (
              <button
                type="button"
                onClick={handleLocalLogin}
                disabled={loading}
                className="w-full h-11 rounded-md bg-primary text-white font-semibold mb-3 hover:opacity-90 disabled:opacity-60"
              >
                {loading ? 'Signing in...' : 'Continue as local admin'}
              </button>
            )}

            <p className="text-xs text-subtle-light dark:text-subtle-dark mb-4">Only pre-registered Google accounts are allowed. If you cannot sign in, contact an administrator.</p>

          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold mb-4">Welcome, {user.name}!</h1>
            <div className="mb-4 p-4 bg-background-light rounded">
              <p className="text-sm text-subtext-light">Email: {user.email}</p>
              {user.picture && <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full mt-2" />}
            </div>
            <button onClick={handleLogout} className="w-full h-10 rounded bg-alert-red text-white font-semibold">Logout</button>
          </>
        )}
      </div>
    </div>
  )
}

export default LoginPage
